import { useState, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { useDispatch, useSelector } from "react-redux";

import Pagination from "../../redux/customHooks/pagination";
import { setFileUrlToOpen, setFileName } from "../../redux/actions/fileViewer";
import { onImageError } from "../../helpers/utils";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const FileViewer = () => {
  const dispatch = useDispatch();

  const fileUrlToOpen = useSelector(
    (state) => state?.fileViewer?.fileUrlToOpen
  );
  const modalToReopen = useSelector(
    (state) => state?.fileViewer?.modalToReopen
  );
  const ImageName = useSelector((state) => state?.fileViewer?.fileName);
  const fileImageAlt = useSelector((state) => state?.fileViewer?.fileImageAlt);
  const fileTitle = useSelector((state) => state?.fileViewer?.fileTitle);

  const [totalPageCount, setTotalPageCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [isImagePreviewUnavailable, setIsImagePreviewUnavailable] =
    useState(false);

  useEffect(() => {
    if (fileUrlToOpen) {
      setTotalPageCount(0);
      setPageNumber(1);
      setIsImagePreviewUnavailable(false);
    }
  }, [fileUrlToOpen]);

  function onDocumentLoadSuccess({ numPages }) {
    setTotalPageCount(numPages);
  }

  function clearFileViewer() {
    dispatch(setFileUrlToOpen(null, null, null, null));
    dispatch(setFileName(null));
    setTotalPageCount(0);
    setPageNumber(1);
    setIsImagePreviewUnavailable(false);
  }

  function getImageDialogTitle() {
    if (fileTitle) {
      return fileTitle;
    }
    if (ImageName) {
      return ImageName;
    }
    return "Dental Scan Image";
  }

  function getImageAltText() {
    if (isImagePreviewUnavailable) {
      return "Image preview unavailable";
    }
    if (fileImageAlt) {
      return fileImageAlt;
    }
    return "Dental scan image";
  }

  function handleImageError(event) {
    setIsImagePreviewUnavailable(true);
    onImageError(event);
    if (event?.currentTarget) {
      event.currentTarget.alt = "Image preview unavailable";
    }
  }

  return (
    <>
      <div
        className="modal fade"
        id="fileViewerImage"
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        aria-labelledby="imageDialogTitle"
        aria-describedby="imageDescription"
      >
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header bg-primary">
              <h2 className="modal-title h2" id="imageDialogTitle">
                {getImageDialogTitle()}
              </h2>
              <button
                type="button"
                className="btn-close file-viewer-close-button"
                data-bs-toggle="modal"
                data-bs-target={modalToReopen}
                aria-label="Close image viewer"
                onClick={clearFileViewer}
              ></button>
            </div>
            <div className="modal-body d-flex justify-content-center">
              <img
                src={fileUrlToOpen}
                onError={handleImageError}
                alt={getImageAltText()}
                id="imageDescription"
              />
            </div>
          </div>
        </div>
      </div>
      <div
        className="modal fade"
        id="fileViewerDoc"
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        aria-labelledby="docDialogTitle"
      >
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header bg-primary">
              <h3 className="modal-title" id="docDialogTitle">
                File{" "}
              </h3>
              {fileUrlToOpen ? (
                <a
                  href={fileUrlToOpen}
                  target="_blank"
                  className="m-2 text-white"
                  aria-label="File downloader"
                  rel="noreferrer"
                >
                  <i className="bi bi-download"></i>
                </a>
              ) : (
                <></>
              )}
              <button
                type="button"
                className="btn-close text-white"
                data-bs-toggle="modal"
                data-bs-target={modalToReopen}
                aria-label="Close document viewer"
                onClick={clearFileViewer}
              ></button>
            </div>
            <div className="modal-body">
              {fileUrlToOpen ? (
                <>
                  <div className="d-flex justify-content-center w-100">
                    <Document
                      file={fileUrlToOpen}
                      onLoadSuccess={onDocumentLoadSuccess}
                      loading={
                        <div className="d-flex justify-content-center my-5">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading PDF...</span>
                          </div>
                        </div>
                      }
                      error="File type not supported for preview. Please download."
                    >
                      <Page
                        pageNumber={pageNumber}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        devicePixelRatio={1}
                      />
                    </Document>
                  </div>
                  <Pagination
                    className="pagination-bar mt-3"
                    currentPage={pageNumber}
                    totalCount={totalPageCount ? totalPageCount : 0}
                    pageSize={1}
                    onPageChange={(page) => setPageNumber(page)}
                    ariaLabel="Document page pagination"
                  />
                </>
              ) : (
                <></>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FileViewer;
